import PropTypes from 'prop-types';
/** @jsx jsx needed to get Storybook to render */
import { css, jsx } from '@emotion/core';
import { Heading3 } from '../../atoms/Headings/Headings';
import Card from '../../atoms/Card/Card';
import Text from '../../atoms/Text/Text';
import sanityImage from '../../../utils/sanityImage';
import {
  breakpoints,
  buttonSpacing,
  colors,
  fontSizes,
  fontWeights,
  pageDimensions,
  shadows,
  unitConverter as uc,
} from '../../../styles/base';
import Image from '../../atoms/Image/Image';
import AnimatedButton from '../../atoms/Buttons/AnimatedButton/AnimatedButton';
import VideoButton from '../../atoms/Buttons/VideoButton/VideoButton';
import Section from '../Section/Section';

// start css
const cardCss = css`
  position: relative;
  display: flex;
  justify-content: center;
  width: ${pageDimensions.desktopWidth};
  height: ${uc('440px')};
  margin: 0 auto;
  overflow: hidden;

  @media (${breakpoints.largeTablet}) {
    width: calc(100% - ${uc('40px')});
    height: auto;
  }
`;

const cardContentCss = css`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 70%;
  margin-left: 37%;
  padding: ${uc('0 160px')};

  @media (${breakpoints.largeTablet}) {
    width: 100%;
    margin-left: 0;
    padding: ${uc('48px')};
    background-color: #fffc;
  }
`;

const heading3Css = css`
  position: relative;
  max-width: ${uc('668px')};
  margin-bottom: ${uc('33px')};
  font-size: ${fontSizes.twentyThree};
`;

const logoCss = css`
  position: absolute;
  top: ${uc('-74px')};
  left: ${uc('-196px')};
  width: ${uc('157px')};
  height: ${uc('157px')};
  background: ${colors.white};
  border-radius: 50%;
  box-shadow: ${shadows.largeHovered};
  transform: translateX(-50%);
  object-fit: contain;
`;

const buttonContainerCss = css`
  position: relative;
  display: flex;

  .video-button {
    margin-right: ${buttonSpacing.marginRight};

    img {
      display: inline-block;
    }
  }

  @media (${breakpoints.mobile}) {
    display: block;

    button,
    a {
      display: block;
      width: 100%;
      margin-right: 0;
    }
  }
`;

// end css

const CarouselCard = ({
  _key,
  animatedButton,
  carouselBg,
  className,
  description,
  headingText,
  logo,
  sectionStyles,
  subheadingText,
  subheadingTextColor,
  videoButton,
}) => {
  const cardBackgroundUrl = sanityImage(carouselBg)
    .bg(colors.lightBlue.replace('#', ''))
    .fit('fill')
    .width(600)
    .auto('format')
    .url();

  const cardBackgroundCss = cardBackgroundUrl
    ? `background-image: url(${cardBackgroundUrl});`
    : `background: ${colors.lightBlue};`;

  const backgroundCss = css`
    position: absolute;
    left: 0;
    width: 41%;
    height: 100%;
    ${cardBackgroundCss}
    background-size: cover;
    clip-path: polygon(0 0, 74% 0, 100% 100%, 0% 100%);

    @media (${breakpoints.largeTablet}) {
      width: 100%;
      clip-path: none;
    }
  `;

  const subheadingTextCss = css`
    margin-bottom: ${uc('10px')};
    color: ${colors[subheadingTextColor]};
    font-weight: ${fontWeights.bold};
  `;

  return (
    <Section sectionStyles={sectionStyles} className={className}>
      <Card css={cardCss} className="carousel-card">
        <div css={backgroundCss} />
        <div css={cardContentCss}>
          <Text css={subheadingTextCss}>{subheadingText}</Text>
          <Heading3 css={heading3Css}>
            {logo && (
              <Image
                css={logoCss}
                src={sanityImage(logo)
                  .width(157)
                  .url()}
                alt={logo.alt}
              />
            )}
            {headingText}
          </Heading3>
          <Text>{description}</Text>
          <div css={buttonContainerCss}>
            {videoButton && (
              <VideoButton className="video-button" {...videoButton}>
                {videoButton.buttonText}
              </VideoButton>
            )}
            {animatedButton && (
              <AnimatedButton _key={`carousel-${_key}`} {...animatedButton}>
                {animatedButton.link.text}
              </AnimatedButton>
            )}
          </div>
        </div>
      </Card>
    </Section>
  );
};

CarouselCard.propTypes = {
  _key: PropTypes.string,
  headingText: PropTypes.string,
  subheadingText: PropTypes.string,
  subheadingTextColor: PropTypes.string,
  description: PropTypes.string,
  carouselBg: PropTypes.shape({}),
  logo: PropTypes.shape({ alt: PropTypes.string }),
  videoButton: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.bool]),
  animatedButton: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.bool]),
  sectionStyles: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({})),
    PropTypes.shape({}),
  ]),
  className: PropTypes.string,
};

CarouselCard.defaultProps = {
  _key: '',
  headingText: '',
  subheadingText: '',
  subheadingTextColor: 'gray',
  description: '',
  carouselBg: {},
  logo: null,
  videoButton: false,
  animatedButton: false,
  sectionStyles: {},
  className: '',
};

export default CarouselCard;
